import { Typography, Button, Progress, Portal } from '@platform/elements';
import React, { createContext, useMemo, useCallback, useEffect, useImperativeHandle, useRef, useState, useContext } from 'react';
import { createEvent } from '@interhyp/platform-eventbus';
import { useFloating, offset, autoUpdate } from '@floating-ui/react';
import { useSpring, animated } from '@react-spring/web';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useIdentity } from '@interhyp/platform-auth';
import { getAPIPath } from '@interhyp/platform-request';
import { createExtensionPoint } from '@interhyp/platform-plugin';

const DEMOPROVIDER_CONTEXT = /*#__PURE__*/createContext({});

const COMPLETE_STEP = createEvent('portal', 'COMPLETE_STEP');

var styles$1 = {"marker":"marker_-r6haXPeGs"};

const sleep = async (milliseconds = 1000) => {
  await new Promise(resolve => {
    setTimeout(() => {
      resolve();
    }, milliseconds);
  });
};

const useAnimationSettings = animation => {
  return useMemo(() => {
    if (animation.type === 'DRAG') {
      return {
        to: async (next, cancel) => {
          await next({
            opacity: 0.65,
            config: {
              tension: 120,
              friction: 14
            }
          });
          await next({
            borderWidth: 13,
            scale: 0.6
          });
          await next({
            '--animation-offset-y': animation.movement.y,
            '--animation-offset-x': animation.movement.x
          });
          await next({
            borderWidth: 6,
            scale: 1
          });
          await next({
            opacity: 0
          });
          await next({
            borderWidth: 6,
            scale: 1,
            '--animation-offset-x': 0,
            '--animation-offset-y': 0,
            immediate: true
          });
        },
        loop: true,
        immediate: true
      };
    }
    if (animation.type === 'SCROLL') {
      return {
        to: async (next, cancel) => {
          await next({
            opacity: 0.65
          });
          await next({
            '--animation-offset-y': animation.movement.y,
            '--animation-offset-x': animation.movement.x
          });
          await sleep(1000);
          await next({
            opacity: 0
          });
          await next({
            borderWidth: 6,
            scale: 1,
            opacity: 0,
            '--animation-offset-x': 0,
            '--animation-offset-y': 0
          });
        },
        loop: true,
        config: {
          tension: 250,
          friction: 80
        },
        immediate: true
      };
    }
    return {
      to: async (next, cancel) => {
        await next({
          borderWidth: 13,
          scale: 0.6,
          opacity: 0.8
        });
        await next({
          borderWidth: 6,
          scale: 1,
          opacity: 0.65
        });
        await next({
          borderWidth: 6,
          scale: 1,
          opacity: 0,
          '--animation-offset-x': 0,
          '--animation-offset-y': 0
        });
      },
      loop: true,
      immediate: true
    };
  }, [animation]);
};

const Marker = /*#__PURE__*/React.forwardRef(({
  targetRef,
  animation
}, ref) => {
  const settings = useAnimationSettings(animation);
  const {
    refs,
    x,
    y,
    strategy
  } = useFloating({
    open: true,
    strategy: 'fixed',
    placement: 'top-start',
    middleware: [offset(({
      rects
    }) => {
      return {
        alignmentAxis: (rects.reference.width ?? 0) * (animation.targetRelativeOffset?.x ?? 0.5) + (animation.targetAbsoluteOffset?.x ?? 0),
        mainAxis: -(rects.reference.height ?? 0) * (animation.targetRelativeOffset?.y ?? 0.5) - (animation.targetAbsoluteOffset?.y ?? 0)
      };
    })],
    whileElementsMounted: (reference, floating, update) => autoUpdate(reference, floating, update, {
      elementResize: false,
      ancestorResize: false,
      animationFrame: true
    })
  });
  const [move, api] = useSpring(() => ({
    loop: true,
    from: {
      '--animation-offset-x': 0,
      '--animation-offset-y': 0,
      scale: 1,
      opacity: 0,
      borderWidth: 6
    },
    config: {
      mass: 1,
      tension: 170
    }
  }), [settings]);
  const setRef = useCallback(ref => {
    refs.setReference(ref);
  }, [refs]);
  useEffect(() => {
    setRef(targetRef.current);
  }, [refs, setRef, targetRef]);
  useEffect(() => {
    api.start(settings);
  }, [api, settings]);
  useImperativeHandle(ref, () => {
    return {
      setRef
    };
  });
  return /*#__PURE__*/jsx(animated.div, {
    ref: refs.setFloating,
    style: {
      top: y ?? 0,
      left: x ?? 0,
      position: strategy,
      display: targetRef.current ? 'block' : 'none',
      ...move
    },
    className: styles$1.marker
  });
});

var styles = {"stepDescription":"stepDescription_gL-kijCMxE","description":"description_TZaeGPDn2G","progress":"progress_dHCFRCjZy3","doneDescription":"doneDescription_R2u3C83DHp"};

function StepDescription({
  done,
  onReset,
  numberOfSteps,
  currentDescription,
  currentStep,
  completionMessage
}) {
  const progress = useMemo(() => {
    if (done) {
      return 100;
    }
    return currentStep / numberOfSteps * 100;
  }, [currentStep, done, numberOfSteps]);
  return /*#__PURE__*/jsxs("div", {
    className: styles.stepDescription,
    children: [done && /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(Typography, {
        className: styles.doneDescription,
        variant: "h4",
        children: completionMessage ?? 'Sie haben die Tour abgeschlossen!'
      }), /*#__PURE__*/jsx(Button, {
        onClick: onReset,
        children: "Zur\xFCck zum Start"
      })]
    }), !done && /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(Progress, {
        value: progress,
        className: styles.progress
      }), /*#__PURE__*/jsx(Typography, {
        margin: false,
        className: styles.description,
        children: currentDescription
      })]
    })]
  });
}

function DemoProvider({
  tour,
  children
}) {
  const steps = useMemo(() => tour?.steps ?? [], [tour?.steps]);
  const marker = useRef(null);
  const [tourDone, setTourDone] = useState(false);
  const [activeDemoStep, setActiveDemoStep] = useState();
  const activeStepIndex = useMemo(() => {
    const index = steps.filter(filterStep => !!filterStep).findIndex(step => activeDemoStep?.id === step?.id);
    if (index < 0) {
      return undefined;
    }
    return index;
  }, [activeDemoStep?.id, steps]);
  useEffect(() => {
    if (tour && !activeDemoStep) {
      setActiveDemoStep(steps?.[0]);
    }
  }, [activeDemoStep, steps, tour]);
  const [registeredSteps, setRegisteredSteps] = useState([]);
  const activeRegisteredStep = useMemo(() => {
    return registeredSteps.find(step => activeDemoStep?.id === step.id);
  }, [activeDemoStep?.id, registeredSteps]);
  const registerStep = useCallback(step => {
    setRegisteredSteps(prev => {
      const stepIsAlreadyRegistered = prev.some(filterStep => filterStep.id === step.id);
      const nowGotRef = step.options.ref.current && !prev.find(findStep => findStep.id === step.id)?.options.ref.current;
      if (stepIsAlreadyRegistered && !nowGotRef) {
        return prev;
      }
      if (stepIsAlreadyRegistered && nowGotRef) {
        return [...prev.filter(findStep => findStep.id !== step.id), step];
      }
      return [...prev, step];
    });
  }, []);
  const unregisterStep = useCallback(step => {
    setRegisteredSteps(prev => prev.filter(filterStep => filterStep.id !== step));
  }, []);
  const onDoneStep = useCallback(id => {
    if (activeStepIndex === undefined || id !== activeDemoStep?.id) {
      return;
    }
    const isLastStep = steps.length - 1 === activeStepIndex;
    if (!isLastStep) {
      setActiveDemoStep(steps[activeStepIndex + 1]);
    } else {
      setTourDone(true);
    }
  }, [activeDemoStep, activeStepIndex, steps]);
  const setRef = useCallback((step, elm) => {
    if (step === activeDemoStep?.id) {
      marker.current?.setRef(elm);
    }
    setRegisteredSteps(prev => {
      return prev.map(registeredStep => {
        if (registeredStep.id === step) {
          registeredStep.options.ref.current = elm;
        }
        return registeredStep;
      });
    });
  }, [activeDemoStep?.id]);
  COMPLETE_STEP.useEvent(id => {
    onDoneStep?.(id.payload);
  });
  const value = useMemo(() => {
    return {
      registerStep,
      onDoneStep,
      unregisterStep,
      setRef
    };
  }, [onDoneStep, registerStep, setRef, unregisterStep]);
  useEffect(() => {
    setTourDone(false);
  }, [tour]);
  const resetTour = useCallback(() => {
    setActiveDemoStep(steps[0]);
    setTourDone(false);
    tour?.onReset?.();
  }, [steps, tour]);
  return /*#__PURE__*/jsxs(DEMOPROVIDER_CONTEXT.Provider, {
    value: value,
    children: [children, /*#__PURE__*/jsxs(Portal, {
      id: "portal-demo-tour",
      children: [activeRegisteredStep && !tourDone && /*#__PURE__*/jsx(Marker, {
        targetRef: activeRegisteredStep.options.ref,
        animation: activeRegisteredStep.options.animation,
        ref: marker
      }, activeRegisteredStep.id.toString()), steps.length > 0 && activeRegisteredStep && activeDemoStep && /*#__PURE__*/jsx(StepDescription, {
        done: tourDone,
        completionMessage: tour?.completionMessage,
        onReset: resetTour,
        numberOfSteps: steps.length,
        currentStep: activeStepIndex ?? 0,
        currentDescription: activeDemoStep.description
      })]
    })]
  });
}

const useDemoStep = ({
  step,
  animation
}) => {
  const ref = useRef(null);
  const {
    registerStep,
    unregisterStep,
    setRef
  } = useContext(DEMOPROVIDER_CONTEXT);
  useEffect(() => {
    if (!step || !registerStep) {
      return;
    }
    registerStep?.({
      id: step.id,
      options: {
        ref,
        animation
      }
    });
    return () => {
      unregisterStep?.(step.id);
    };
  }, [registerStep, animation, step, unregisterStep]);
  const innerSetRef = useCallback(elm => {
    ref.current = elm;
    setRef?.(step.id, elm);
  }, [setRef, step.id]);
  return {
    ref,
    setRef: innerSetRef
  };
};

const useDemoStepDone = step => {
  const ref = useRef(null);
  const {
    onDoneStep
  } = useContext(DEMOPROVIDER_CONTEXT);
  const onDone = useCallback(() => {
    onDoneStep?.(step.id);
  }, [onDoneStep, step]);
  return {
    ref,
    onDone
  };
};

async function fetchDemoTourConfig({
  entityType,
  entityId,
  companyId
}) {
  const configName = 'portal-demo-tour';
  const uri = `${getAPIPath('configuration')}/v1/companies/${companyId}/configuration-entries/${configName}?entity-type=${entityType.toUpperCase()}&entity-id=${entityId}&inherit=ALL`;
  try {
    const response = await fetch(uri);
    if (!response.ok) {
      return undefined;
    }
    const json = await response.json();
    return json.value;
  } catch {
    return undefined;
  }
}

const demoTourPlugin = createExtensionPoint(Symbol('demo-tour'), true);

const useActiveDemoTour = overrideId => {
  const [tourId, setTourId] = useState(overrideId);
  const tours = demoTourPlugin.useExtensions();
  const identity = useIdentity();
  useEffect(() => {
    if (!identity) {
      return;
    }
    const fetchTour = async () => {
      const id = await fetchDemoTourConfig({
        companyId: identity.companyId,
        entityId: identity.userId,
        entityType: 'USER'
      });
      setTourId(id);
    };
    if (!overrideId) {
      void fetchTour();
    }
  }, [identity, overrideId]);
  return useMemo(() => {
    return tours.find(tour => tour.id === tourId);
  }, [tourId, tours]);
};

const createDemoStep = ({
  id,
  description
}) => {
  return {
    id: Symbol(id),
    description
  };
};

export { COMPLETE_STEP, DemoProvider, createDemoStep, demoTourPlugin, useActiveDemoTour, useDemoStep, useDemoStepDone };
//# sourceMappingURL=index.js.map
